/**
 * Per-import status-summary filter (hidden segments). View preference keyed by
 * import id — same persistence model as {@link import-sort.ls}.
 */
import type { StatusSummaryGroupKey } from './candidate-status.helpers';

const STORAGE_VERSION = 1;

const VALID_KEYS = new Set<StatusSummaryGroupKey>([
  'mapped',
  'incomplete',
  'discarded',
]);

const storageKey = (importId: string): string =>
  `nodon.import-status-filter.v${STORAGE_VERSION}.${importId}`;

const isStatusSummaryGroupKey = (
  value: unknown,
): value is StatusSummaryGroupKey =>
  typeof value === 'string' && VALID_KEYS.has(value as StatusSummaryGroupKey);

const isHiddenGroupList = (value: unknown): value is StatusSummaryGroupKey[] =>
  Array.isArray(value) && value.every(isStatusSummaryGroupKey);

/** Persisted hidden summary segments for an import, or empty when none stored. */
export const loadImportStatusFilter = (
  importId: string,
): ReadonlySet<StatusSummaryGroupKey> => {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(storageKey(importId));
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    return isHiddenGroupList(parsed) ? new Set(parsed) : new Set();
  } catch {
    return new Set();
  }
};

/** Persist (or clear when empty) hidden summary segments for an import. */
export const saveImportStatusFilter = (
  importId: string,
  hiddenGroups: ReadonlySet<StatusSummaryGroupKey>,
): void => {
  if (typeof window === 'undefined') return;
  try {
    if (hiddenGroups.size === 0) {
      window.localStorage.removeItem(storageKey(importId));
      return;
    }
    window.localStorage.setItem(
      storageKey(importId),
      JSON.stringify([...hiddenGroups]),
    );
  } catch {
    // Quota / disabled storage — safe to drop the preference.
  }
};
